import React, { useState, useRef, useContext } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { PrimaryButton, useAlert } from "@wit/mpesa-ui-components";
import { ConfigContext } from "../../../../app.component";
import { MAX_CSV_FILE_SIZE } from "../../../../shared/shared.constants";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import UsersApi from "../../users/users.api";
import { VALID_PREFIX_MSIDN } from "../../../../shared/shared.utils";
import CloudUploadIcon from "../../../../shared/icons/upload-cloid.icon";
import CsvFileUploadIcon from "../../../../shared/icons/csv-file-upload.icon";

interface ITaskDeclineModalProps {
  cancelAction: () => void;
  confirmAction: (reason: string) => void;
}

const WhiteListUploadModal = ({ cancelAction, confirmAction }: ITaskDeclineModalProps) => {
  // Hooks initialization
  const [t] = useTranslation();
  const refBusinessAgent = useRef(null);
  const { config } = useContext(ConfigContext);
  const MAX_FILE_SIZE = config?.MAX_CSV_FILE_SIZE ? config?.MAX_CSV_FILE_SIZE : MAX_CSV_FILE_SIZE;
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isValidated, setIsValidated] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setCsvFile(null);
    setIsValidated(false);
    setUploadProgress(0);
    hideAlert();
    setIsValidated(false);
    if (!file) {
      return;
    }

    if (!file.name.endsWith(".csv")) {
      showError(t("pages.whitelistUploadModal.invalidFileType"));
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showError(`File size exceeds maximum limit (${(MAX_FILE_SIZE / 1024 / 1024).toFixed(2)} MB).`);
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result as string;
      if (!text) {
        showError(t("pages.whitelistUploadModal.emptyFile"));
        return;
      }

      const rows = text.trim().split(/\r?\n/);
      if (rows.length < 2) {
        showError(t("pages.whitelistUploadModal.invalidRowCount"));
        return;
      }

      const header = rows[0].trim().toUpperCase();
      if (header !== "MSISDN") {
        showError(t("pages.whitelistUploadModal.invalidHeader"));
        return;
      }

      const invalidRows: string[] = [];
      const sampleRows = rows.slice(1, 6);
      sampleRows.forEach((row, idx) => {
        const msisdn = row.trim();

        if (!/^\d+$/.test(msisdn)) {
          invalidRows.push(t("pages.whitelistUploadModal.nonNumeric", { row: idx + 2, msisdn }));
          return;
        }

        if (msisdn.length !== 9) {
          invalidRows.push(
            t("pages.whitelistUploadModal.invalidLength", {
              row: idx + 2,
              length: msisdn.length,
            }),
          );
          return;
        }

        if (!VALID_PREFIX_MSIDN.validPrefixesRegex.test(msisdn)) {
          invalidRows.push(t("pages.whitelistUploadModal.invalidPrefix", { row: idx + 2, msisdn }));
        }
      });

      if (invalidRows.length > 0) {
        const formattedErrors = invalidRows.map(err => `• ${err}`).join("\n");
        showError(`${t("pages.whitelistUploadModal.uploadValidate")}\n${formattedErrors}`);
        return;
      }

      setCsvFile(file);
      setIsValidated(true);
    };

    reader.readAsText(file);
  };

  const showError = (msg: string) => {
    setAlertProps({
      title: msg,
      type: AlertTypeEnum.ERROR,
    });
    showAlert();
    setTimeout(() => {
      hideAlert();
    }, 40000);
  };

  const uploadWhitelistFile = () => {
    if (!csvFile) {
      showError(t("pages.whitelistUploadModal.uploadValidate"));
      return;
    }
    UsersApi.methods
      .uploadWhitelistFile(csvFile, (progressEvent: ProgressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(percent);
      })
      .then(() => {
        setAlertProps({
          title: t("pages.whitelistUploadModal.uploadSuccess"),
          type: AlertTypeEnum.SUCCESS,
        });
        showAlert();
        setCsvFile(null);
        setUploadProgress(0);
        cancelAction();
      })
      .catch(() => {
        showError(t("pages.whitelistUploadModal.uploadError"));
        setUploadProgress(0);
        cancelAction();
      });
  };

  const handleClick = () => {
    inputRef.current?.click();
  };
  return (
    <TaskDeclineForm id="taskDeclineForm">
      <Title>{t("pages.whitelistUploadModal.tile")}</Title>
      <div style={{ marginTop: "6%" }}>
        <UploadFileContainer ref={refBusinessAgent}>
          <UploadWrapper onClick={handleClick}>
            <IconContainer>{csvFile ? <CsvFileUploadIcon /> : <CloudUploadIcon />}</IconContainer>
            {csvFile ? <FileName>{csvFile.name}</FileName> : <UploadText>Click to upload file</UploadText>}
          </UploadWrapper>
          <HiddenInput ref={inputRef} type="file" onChange={handleFileChange} />
        </UploadFileContainer>
      </div>
      {uploadProgress > 0 && (
        <ProgressBarWrapper>
          <ProgressBarBackground>
            <ProgressBarFill style={{ width: `${uploadProgress}%` }} />
          </ProgressBarBackground>
          <ProgressPercent>{uploadProgress}%</ProgressPercent>
        </ProgressBarWrapper>
      )}
      <TaskDeclineButtonsWrapper>
        <TaskDeclineButtonDiv>
          <PrimaryButton
            id={"cancel-reject-button"}
            titleLabel={t("pages.whitelistUploadModal.cancelBtn")}
            onClick={() => {
              cancelAction();
            }}
            redTheme={false}
            loading={false}
            style={{ width: "111px" }}
          />
        </TaskDeclineButtonDiv>
        <TaskDeclineButtonDiv>
          <PrimaryButton
            id={"confirm-reject-button"}
            titleLabel={t("pages.whitelistUploadModal.uploadBtn")}
            onClick={uploadWhitelistFile}
            redTheme={true}
            loading={false}
            disabled={!isValidated}
            style={{ width: "100px" }}
          />
        </TaskDeclineButtonDiv>
      </TaskDeclineButtonsWrapper>
    </TaskDeclineForm>
  );
};

export default WhiteListUploadModal;

const TaskDeclineForm = styled("div")`
  display: flex;
  padding: 24px;
  width: 100%;
  height: 100%;
  flex-direction: column;
  position: relative;
`;

const TaskDeclineButtonDiv = styled("div")`
  margin-right: 12px;
  &:last-of-type {
    margin-right: 0;
  }
`;
const TaskDeclineButtonsWrapper = styled("div")`
  position: absolute;
  bottom: 24px;
  right: 24px;
  display: flex;
  justify-content: flex-end;
  width: calc(100% - 48px); /* 24px padding on both sides */
`;

const Title = styled("div")`
  font-family: Vodafone Rg;
  font-size: 22px;
  color: ${props => props.theme.palette.darkGrey};
  margin-bottom: 13px;
`;
const Description = styled("div")`
  font-family: Vodafone Rg;
  font-size: 18px;
  color: ${props => props.theme.palette.darkGrey};
  margin-bottom: 24px;
`;
const UploadFileContainer = styled("div")`
  display: flex;
  flex-direction: column;
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
`;
const ProgressBarWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const ProgressBarBackground = styled.div`
  background-color: #e0e0e0;
  height: 8px;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: #009e0f;
  transition: width 0.3s ease-in-out;
`;

const ProgressPercent = styled.div`
  text-align: right;
  font-size: 12px;
  color: #333;
  margin-top: 4px;
`;

const UploadWrapper = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  background: #f8fafc;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2563eb;
    background: #eff6ff;
  }
`;

const UploadText = styled.span`
  font-size: 14px;
  color: #475569;
`;

const FileName = styled.span`
  font-size: 14px;
  color: #0f172a;
  font-weight: 500;
`;

const HiddenInput = styled.input`
  display: none;
`;

const IconContainer = styled("div")`
  stroke: ${props => props.theme.palette.vodafoneRed};
  width: 20px;
  height: 20px;
  margin-left: 0px;

  svg {
    height: 20px;
  }
`;
